/**
 * Mount de la pizarra `/operaciones.html` — Fase 8b/8c/8d + sidebar
 * reestructurado.
 *
 * Flujo:
 *  1. mountChrome con activeSection='operaciones'.
 *  2. Llama a `GET /api/operations` y reparte papeles entre zonas.
 *  3. Para officer+, monta el bloque ACCIONES en el sidebar derecho
 *     con dos grupos (CAMPAÑAS, MISIONES) y wiring de los dropdowns
 *     EDITAR/BORRAR.
 *  4. Errores → banner discreto sobre la pizarra.
 *
 * Read-only en lo visual: papeles inertes. La interacción con papeles
 * se añade en 8e.
 */

import type {
  AuthenticatedPilot,
  CampaignSummary,
  MissionSummary,
  OperationsResponse,
} from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import { deleteCampaign } from '../campania-editor/api.js';
import { deleteMission } from '../mision-editor/api.js';
import { fetchOperations } from './api.js';
import { renderBoardShell, renderOperacionesActions } from './markup.js';
import { renderCampaignPaper, renderMissionPaper } from './papers.js';

// Rutas de detalle (Fase 8f.1). Si en algún momento cambian de nombre
// se tocan solo aquí.
const ROUTE_CAMPAIGN_DETAIL = '/oper_cam.html';
const ROUTE_MISSION_DETAIL = '/oper_mis.html';

import './operaciones.css';

const OFFICER_ROLES: ReadonlyArray<AuthenticatedPilot['role']> = [
  'officer',
  'admin',
  'superadmin',
];

export async function mountOperaciones(): Promise<void> {
  const { contentRoot, user } = await mountChrome({
    activeSection: 'operaciones',
    allowAnonymous: true,
  });
  contentRoot.innerHTML = renderBoardShell();
  wirePaperClicks(contentRoot);

  // Solo officer+ con sesión ve el bloque ACCIONES (crear/editar/borrar).
  // Los anónimos (`user === null`) ven la pizarra en modo solo lectura.
  const isOfficer = user !== null && OFFICER_ROLES.includes(user.role);

  const result = await fetchOperations();
  if (!result.ok) {
    renderError(contentRoot, result.message);
    if (isOfficer) {
      // Si la API falla, pintamos el sidebar con listas vacías para que
      // al menos NUEVA CAMPAÑA/MISIÓN sigan accesibles.
      mountActions(contentRoot, [], []);
    }
    return;
  }
  renderZones(contentRoot, result.data);
  if (isOfficer) {
    mountActions(contentRoot, result.data.campaigns, result.data.missions);
  }
}

/**
 * Click delegation sobre las zonas de papeles. Una sola escucha en el
 * `.board-stage` se encarga de:
 *  - Campaña → `/oper_cam.html?id=<id>` (Fase 8f.1)
 *  - Misión  → `/oper_mis.html?id=<id>` SIEMPRE.
 *
 * Antes la pizarra saltaba al briefing si la misión tenía uno asociado.
 * Cambio del 2026-05-12: ahora SIEMPRE se va al detalle de la misión,
 * y la propia hoja del detalle ofrece un botón VER BRIEFING si existe.
 *
 * El handler también acepta Enter/Espacio para accesibilidad (los
 * papeles son `role="button"` con `tabindex="0"`).
 */
function wirePaperClicks(root: HTMLElement): void {
  const stage = root.querySelector<HTMLElement>('.board-stage');
  if (!stage) return;

  const handle = (target: HTMLElement) => {
    const paper = target.closest<HTMLElement>('.paper');
    if (!paper) return;
    const kind = paper.dataset['paperKind'];
    const id = paper.dataset['paperId'];
    if (!id) return;

    if (kind === 'mission') {
      window.location.href = `${ROUTE_MISSION_DETAIL}?id=${encodeURIComponent(id)}`;
      return;
    }
    if (kind === 'campaign') {
      window.location.href = `${ROUTE_CAMPAIGN_DETAIL}?id=${encodeURIComponent(id)}`;
    }
  };

  stage.addEventListener('click', (ev) => {
    handle(ev.target as HTMLElement);
  });
  stage.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const t = ev.target as HTMLElement;
    if (!t.classList?.contains('paper')) return;
    ev.preventDefault();
    handle(t);
  });
}

function mountActions(
  root: HTMLElement,
  campaigns: CampaignSummary[],
  missions: MissionSummary[],
): void {
  replaceActions(renderOperacionesActions(campaigns, missions));
  wireEditDropdowns();
  wireDeleteDropdowns(root, campaigns, missions);
}

/** Reemplaza el `.chrome-actions` por defecto por el de la pizarra. */
function replaceActions(html: string): void {
  const host = document.querySelector<HTMLElement>(
    '.chrome-sidebar--right .chrome-actions',
  );
  if (host) host.outerHTML = html;
}

/**
 * EDITAR: al elegir una opción del select, navegamos al editor con
 * `?id=`. Reseteamos el value al placeholder antes de navegar para
 * evitar quedarnos con la selección al volver con `back`.
 */
function wireEditDropdowns(): void {
  const editCampaign = document.querySelector<HTMLSelectElement>(
    '[data-board-action="edit-campaign"]',
  );
  editCampaign?.addEventListener('change', () => {
    const id = editCampaign.value;
    if (!id) return;
    window.location.href = `/operaciones/nueva-campania.html?id=${encodeURIComponent(id)}`;
  });

  const editMission = document.querySelector<HTMLSelectElement>(
    '[data-board-action="edit-mission"]',
  );
  editMission?.addEventListener('change', () => {
    const id = editMission.value;
    if (!id) return;
    window.location.href = `/operaciones/nueva-mision.html?id=${encodeURIComponent(id)}`;
  });
}

/**
 * BORRAR: pide confirmación con el nombre de la entidad. En éxito,
 * recarga la página completa para que la pizarra refleje los cambios
 * y los selects vuelvan a poblarse.
 */
function wireDeleteDropdowns(
  root: HTMLElement,
  campaigns: CampaignSummary[],
  missions: MissionSummary[],
): void {
  const campMap = new Map(campaigns.map((c) => [c.id, c]));
  const misMap = new Map(missions.map((m) => [m.id, m]));

  const delCampaign = document.querySelector<HTMLSelectElement>(
    '[data-board-action="delete-campaign"]',
  );
  delCampaign?.addEventListener('change', async () => {
    const id = delCampaign.value;
    if (!id) return;
    const camp = campMap.get(id);
    delCampaign.value = '';
    if (!camp) return;
    const ok = await openConfirmModal({
      title: 'Borrar campaña',
      message:
        `¿Seguro que quieres borrar la campaña "${camp.name}"? ` +
        `Sus misiones quedarán sueltas (campaignId=null). ` +
        `Esta acción no se puede deshacer.`,
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    const res = await deleteCampaign(id);
    if (!res.ok) {
      renderError(root, `No se pudo borrar: ${res.message}`);
      return;
    }
    window.location.reload();
  });

  const delMission = document.querySelector<HTMLSelectElement>(
    '[data-board-action="delete-mission"]',
  );
  delMission?.addEventListener('change', async () => {
    const id = delMission.value;
    if (!id) return;
    const mis = misMap.get(id);
    delMission.value = '';
    if (!mis) return;
    const ok = await openConfirmModal({
      title: 'Borrar misión',
      message:
        `¿Seguro que quieres borrar la misión "${mis.name}"? ` +
        `Esta acción no se puede deshacer.`,
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    const res = await deleteMission(id);
    if (!res.ok) {
      renderError(root, `No se pudo borrar: ${res.message}`);
      return;
    }
    window.location.reload();
  });
}

function renderZones(root: HTMLElement, data: OperationsResponse): void {
  const closedZone = root.querySelector<HTMLElement>('[data-zone="closed"]');
  const activeZone = root.querySelector<HTMLElement>('[data-zone="active"]');
  if (!closedZone || !activeZone) return;

  const { closedHtml, activeHtml } = classify(data.campaigns, data.missions);
  closedZone.innerHTML = closedHtml;
  activeZone.innerHTML = activeHtml;
}

interface ZoneSplit {
  closedHtml: string;
  activeHtml: string;
}

function classify(
  campaigns: CampaignSummary[],
  missions: MissionSummary[],
): ZoneSplit {
  const closedParts: string[] = [];
  const activeParts: string[] = [];

  for (const c of campaigns) {
    const html = renderCampaignPaper(c);
    if (c.status === 'finished') closedParts.push(html);
    else activeParts.push(html);
  }
  for (const m of missions) {
    const html = renderMissionPaper(m);
    // Solo `flown` pasa a closed: ya se voló y no se vuelve a volar.
    // `cancelled` se queda en active porque conceptualmente es una
    // misión re-programable (cancelada por meteo, etc.) que sigue
    // pendiente de volar con una nueva fecha. `planned` también activa.
    if (m.status === 'flown') closedParts.push(html);
    else activeParts.push(html);
  }

  return {
    closedHtml: closedParts.join(''),
    activeHtml: activeParts.join(''),
  };
}

function renderError(root: HTMLElement, message: string): void {
  const stage = root.querySelector<HTMLElement>('.board-stage');
  if (!stage) return;
  const banner = document.createElement('div');
  banner.className = 'board-error';
  banner.textContent = message;
  stage.appendChild(banner);
}
